import * as React from "react";
import {
  createFileRoute,
  Outlet,
  redirect,
  retainSearchParams,
  useNavigate,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { toast } from "sonner";
import { z } from "zod";

import {
  getGatewayLoginUrl,
  getGatewaySessionToken,
} from "@/common/gateway-session";
import { runOutboundSignupAttributionAttempt } from "@/common/outbound-signup-attribution";
import { FullScreenLoader } from "@/components/FullScreenLoader";
import { SettingsDialog } from "@/components/settings/SettingsDialog";
import { SubscriptionDialog } from "@/features/billing/components/SubscriptionDialog";
import { useCrisp } from "@/hooks/useCrisp";
import { useUser as useSessionUser } from "@/hooks/useUser";
import { api } from "@/trpc/react";

const authenticatedSearchSchema = z.object({
  /** When true, opens the subscription dialog */
  sub: z.boolean().optional(),
  /** Reason the subscription dialog was opened (e.g. "insufficient_credits") */
  sub_reason: z.string().optional(),
  /** Set by OAuth callback redirects (instagram | tiktok) */
  oauth_provider: z.string().optional(),
  /** Error message from failed OAuth callback */
  oauth_error: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated")({
  ssr: false,
  head: () => ({
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  validateSearch: zodValidator(authenticatedSearchSchema),
  search: {
    middlewares: [retainSearchParams(["sub", "sub_reason"])],
  },
  // Before loading, authenticate the user: auth is "does /session/token yield
  // a token". Forward the requested URL so login lands the user back where
  // they aimed.
  beforeLoad: async ({ location, preload }) => {
    // Preload (defaultPreload: "intent") must not run the auth redirect — the
    // gateway hand-off uses reloadDocument, which would hard-navigate the whole
    // page on hover. The real navigation re-runs beforeLoad without preload.
    // Mirrors admin.route.
    if (preload) return;
    const token = await getGatewaySessionToken();
    if (!token) {
      // getGatewayLoginUrl returns a RELATIVE path (/auth/start...). TanStack
      // Router treats a relative href as an internal client navigation, but
      // /auth/start is a server-only Nitro route with no client route, so the
      // SPA would render not-found and the OAuth handshake would never run.
      // reloadDocument forces a real document GET → server 302 → gateway auth.
      throw redirect({
        href: getGatewayLoginUrl(location.href),
        reloadDocument: true,
      });
    }
  },
  loader: ({ context: { trpcQueryUtils } }) => {
    // Prefetch pricing data for SubscriptionDialog (non-blocking)
    void trpcQueryUtils.payments.listPlans.prefetch().catch(console.error);
    void trpcQueryUtils.payments.getCurrentAccountPlan
      .prefetch()
      .catch(console.error);
    void trpcQueryUtils.payments.getCurrentAccountPlanDetails
      .prefetch()
      .catch(console.error);
    void trpcQueryUtils.payments.getCurrentAccountSubscription
      .prefetch()
      .catch(console.error);
  },
  pendingComponent: FullScreenLoader,
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { sub, sub_reason, oauth_provider, oauth_error } = Route.useSearch();
  const navigate = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = React.useState(false);
  const [settingsSection, setSettingsSection] = React.useState<
    "connections" | undefined
  >(undefined);

  useCrisp();

  const user = useSessionUser();
  const recordOutboundAttribution =
    api.account.recordOutboundSignupAttribution.useMutation();
  const accountId = user.user?.publicMetadata.accountId;
  const accountCreatedAt = user.user?.createdAt;
  const [settledOutboundAttributionAccountId, setSettledAccountId] =
    React.useState<string | null>(null);
  const outboundAttributionAttempt = React.useRef<{
    accountId: string;
    promise: Promise<void>;
  } | null>(null);

  React.useEffect(() => {
    if (!user.isLoaded) return;
    if (!accountId) return;
    if (!accountCreatedAt) {
      setSettledAccountId(accountId);
      return;
    }

    const existingAttempt = outboundAttributionAttempt.current;
    const attempt =
      existingAttempt?.accountId === accountId
        ? existingAttempt.promise
        : runOutboundSignupAttributionAttempt({
            accountCreatedAt,
            getStorage: () =>
              typeof window === "undefined" ? undefined : window.localStorage,
            record: (attribution) =>
              recordOutboundAttribution.mutateAsync(attribution),
          });
    outboundAttributionAttempt.current = { accountId, promise: attempt };

    let active = true;
    void attempt.finally(() => {
      if (active) setSettledAccountId(accountId);
    });
    return () => {
      active = false;
    };
    // The mutation object is not referentially stable. Account identity is the
    // intended trigger; the server's set-once row makes StrictMode retries safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isLoaded, accountId, accountCreatedAt?.getTime()]);

  // Handle OAuth callback redirect params (instagram / tiktok).
  // Skip when on the account pages — account layout handles it there.
  // Elsewhere (incl. /onboarding) this global handler shows the toast and
  // clears the params; the onboarding connect step no longer does its own.
  React.useEffect(() => {
    if (!oauth_provider) return;
    if (window.location.pathname.startsWith("/account")) return;

    const displayName =
      oauth_provider === "tiktok"
        ? "TikTok"
        : oauth_provider === "meta"
          ? "Meta Ads"
          : "Instagram";

    if (oauth_error) {
      toast.error(`${displayName} connection failed: ${oauth_error}`);
    } else {
      toast.success(`${displayName} account connected`);
    }

    setSettingsSection("connections");
    setIsSettingsOpen(true);

    void navigate({
      to: ".",
      search: { oauth_provider: undefined, oauth_error: undefined },
      replace: true,
    });
  }, [oauth_provider, oauth_error, navigate]);

  const handleSubOpenChange = (open: boolean) => {
    if (!open) {
      void navigate({
        to: ".",
        search: { sub: undefined, sub_reason: undefined },
      });
    }
  };

  if (
    !user.isLoaded ||
    (accountId && settledOutboundAttributionAccountId !== accountId)
  ) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <Outlet />
      <SubscriptionDialog
        open={sub ?? false}
        onOpenChange={handleSubOpenChange}
        reason={sub_reason}
      />
      <SettingsDialog
        isOpen={isSettingsOpen}
        setIsOpen={(open) => {
          setIsSettingsOpen(open);
          if (!open) setSettingsSection(undefined);
        }}
        defaultSection={settingsSection}
      />
    </>
  );
}
